import { DateRange, formatDateUTC } from './dateUtils';
import { DailyProfitSummary } from '../api/profit';

/**
 * Comparison period types
 */
export type ComparisonType = 'previous' | 'lastYear' | 'custom' | null;

/**
 * Metric configuration for comparisons
 */
export interface MetricConfig {
  key: string;
  label: string;
  invertLogic: boolean; // true if lower values are better (e.g., fee ratio)
  formatType: 'currency' | 'percentage' | 'count';
}

/**
 * Result of comparing a single metric between two periods
 */
export interface MetricComparison {
  name: string;
  current: number;
  comparison: number;
  absoluteChange: number;
  percentageChange: number;
  verdict: 'better' | 'same' | 'worse';
  formatType: 'currency' | 'percentage' | 'count';
  invertLogic: boolean; // true if lower values are better (affects arrow direction)
}

/**
 * Metrics to compare between periods
 */
export const METRICS_CONFIG: MetricConfig[] = [
  { key: 'net_profit', label: 'Net Profit', invertLogic: false, formatType: 'currency' },
  { key: 'avg_daily_profit', label: 'Avg Daily Profit', invertLogic: false, formatType: 'currency' },
  { key: 'total_trades', label: 'Total Trades', invertLogic: false, formatType: 'count' },
  { key: 'win_rate', label: 'Win Rate', invertLogic: false, formatType: 'percentage' },
  { key: 'profit_per_trade', label: 'Profit/Trade', invertLogic: false, formatType: 'currency' },
  { key: 'fee_ratio', label: 'Fee Ratio', invertLogic: true, formatType: 'percentage' }, // Lower is better
];

// ===== Date Range Calculation Functions =====

/**
 * Calculate the length of a date range in days (inclusive)
 * @param range - Date range to calculate
 * @returns Number of days in the range (inclusive of both endpoints)
 */
export function calculateRangeLengthDays(range: DateRange): number {
  const start = new Date(range.start_date + 'T00:00:00Z');
  const end = new Date(range.end_date + 'T00:00:00Z');
  
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  return diffDays + 1; // +1 to include both endpoints
}

/**
 * Calculate the "Previous Period" comparison range
 * Returns a range of equal length ending 1 day before the current start date
 * 
 * Example:
 * - Current: Oct 1-30 (30 days)
 * - Previous: Sep 1-30 (30 days before current start)
 * 
 * @param current - Current date range
 * @returns Comparison date range
 */
export function calculatePreviousPeriod(current: DateRange): DateRange {
  const currentStart = new Date(current.start_date + 'T00:00:00Z');

  const lengthDays = calculateRangeLengthDays(current);
  
  // Comparison ends 1 day before current starts
  const comparisonEnd = new Date(Date.UTC(
    currentStart.getUTCFullYear(),
    currentStart.getUTCMonth(),
    currentStart.getUTCDate() - 1
  ));
  
  // Comparison starts lengthDays before comparison end
  const comparisonStart = new Date(Date.UTC(
    comparisonEnd.getUTCFullYear(),
    comparisonEnd.getUTCMonth(),
    comparisonEnd.getUTCDate() - lengthDays + 1
  ));
  
  return {
    start_date: formatDateUTC(comparisonStart),
    end_date: formatDateUTC(comparisonEnd)
  };
}

/**
 * Calculate the "Same Period Last Year" comparison range
 * Returns a range with the same dates, exactly 1 year ago
 * 
 * Example:
 * - Current: Oct 1-30, 2025
 * - Comparison: Oct 1-30, 2024
 * 
 * @param current - Current date range
 * @returns Comparison date range
 */
export function calculateSamePeriodLastYear(current: DateRange): DateRange {
  const currentStart = new Date(current.start_date + 'T00:00:00Z');
  const currentEnd = new Date(current.end_date + 'T00:00:00Z');
  
  const comparisonStart = new Date(Date.UTC(
    currentStart.getUTCFullYear() - 1,
    currentStart.getUTCMonth(),
    currentStart.getUTCDate()
  ));
  
  const comparisonEnd = new Date(Date.UTC(
    currentEnd.getUTCFullYear() - 1,
    currentEnd.getUTCMonth(),
    currentEnd.getUTCDate()
  ));
  
  return {
    start_date: formatDateUTC(comparisonStart),
    end_date: formatDateUTC(comparisonEnd)
  };
}

// ===== Metric Comparison Functions =====

/**
 * Calculate absolute change between current and comparison values
 * @param current - Current period value
 * @param comparison - Comparison period value
 * @returns Absolute difference (current - comparison)
 */
export function calculateAbsoluteChange(current: number, comparison: number): number {
  return current - comparison;
}

/**
 * Calculate percentage change between current and comparison values
 * @param current - Current period value
 * @param comparison - Comparison period value
 * @returns Percentage change
 */
export function calculatePercentageChange(current: number, comparison: number): number {
  if (comparison === 0) {
    // Handle division by zero
    return current > 0 ? 100 : (current < 0 ? -100 : 0);
  }
  return ((current - comparison) / Math.abs(comparison)) * 100;
}

/**
 * Determine verdict for a metric comparison
 * @param percentChange - Percentage change value
 * @param invertLogic - If true, negative change is "better" (for metrics like fee ratio)
 * @returns Verdict: 'better', 'same', or 'worse'
 */
export function determineVerdict(
  percentChange: number,
  invertLogic: boolean = false
): 'better' | 'same' | 'worse' {
  const threshold = 5; // 5% threshold for "same"
  
  // If change is within threshold, consider it the same
  if (Math.abs(percentChange) < threshold) {
    return 'same';
  }
  
  const isPositiveChange = percentChange > 0;
  const isBetter = invertLogic ? !isPositiveChange : isPositiveChange;
  
  return isBetter ? 'better' : 'worse';
}

/**
 * Calculate aggregated metrics from daily profit data
 * @param data - Array of daily profit summaries
 * @returns Aggregated metrics object
 */
export function calculateAggregatedMetrics(data: DailyProfitSummary[]): {
  net_profit: number;
  avg_daily_profit: number;
  total_trades: number;
  win_rate: number;
  profit_per_trade: number;
  fee_ratio: number;
} {
  if (!data || data.length === 0) {
    return {
      net_profit: 0,
      avg_daily_profit: 0,
      total_trades: 0,
      win_rate: 0,
      profit_per_trade: 0,
      fee_ratio: 0
    };
  }

  const net_profit = data.reduce((sum, day) => sum + day.net_profit, 0);
  const gross_profit = data.reduce((sum, day) => sum + day.gross_profit, 0);
  const total_trades = data.reduce((sum, day) => sum + day.trade_count, 0);
  const fees_paid = data.reduce((sum, day) => sum + (day.fees_paid || 0), 0);
  
  const avg_daily_profit = net_profit / data.length;
  
  // Calculate win rate (percentage of days with positive profit)
  const profitableDays = data.filter(day => day.net_profit > 0).length;
  const win_rate = (profitableDays / data.length) * 100;
  
  // Calculate profit per trade
  const profit_per_trade = total_trades > 0 ? net_profit / total_trades : 0;
  
  // Calculate fee ratio (fees as percentage of gross profit)
  const fee_ratio = gross_profit > 0 ? (fees_paid / gross_profit) * 100 : 0;

  return {
    net_profit,
    avg_daily_profit,
    total_trades,
    win_rate,
    profit_per_trade,
    fee_ratio
  };
}

/**
 * Compare metrics between current and comparison periods
 * @param currentData - Daily profit data for current period
 * @param comparisonData - Daily profit data for comparison period
 * @returns Array of metric comparisons
 */
export function compareMetrics(
  currentData: DailyProfitSummary[],
  comparisonData: DailyProfitSummary[]
): MetricComparison[] {
  const currentMetrics = calculateAggregatedMetrics(currentData);
  const comparisonMetrics = calculateAggregatedMetrics(comparisonData);

  return METRICS_CONFIG.map((config) => {
    const currentValue = currentMetrics[config.key as keyof typeof currentMetrics];
    const comparisonValue = comparisonMetrics[config.key as keyof typeof comparisonMetrics];
    
    const absoluteChange = calculateAbsoluteChange(currentValue, comparisonValue);
    const percentageChange = calculatePercentageChange(currentValue, comparisonValue);
    const verdict = determineVerdict(percentageChange, config.invertLogic);

    return {
      name: config.label,
      current: currentValue,
      comparison: comparisonValue,
      absoluteChange,
      percentageChange,
      verdict,
      formatType: config.formatType,
      invertLogic: config.invertLogic
    };
  });
}

/**
 * Generate overall performance summary from metric comparisons
 * @param comparisons - Array of metric comparisons
 * @returns Summary text describing overall performance
 */
export function generatePerformanceSummary(comparisons: MetricComparison[]): string {
  const betterCount = comparisons.filter(c => c.verdict === 'better').length;
  const worseCount = comparisons.filter(c => c.verdict === 'worse').length;
  const total = comparisons.length;
  
  let summary = '';
  
  if (betterCount > worseCount) {
    summary = `⭐ Outperforming previous period on ${betterCount}/${total} metrics`;
  } else if (worseCount > betterCount) {
    summary = `⚠️ Underperforming previous period on ${worseCount}/${total} metrics`;
  } else {
    summary = `Performance similar to previous period`;
  }
  
  // Find biggest improvement
  const biggestImprovement = comparisons
    .filter(c => c.verdict === 'better')
    .sort((a, b) => Math.abs(b.percentageChange) - Math.abs(a.percentageChange))[0];
  
  if (biggestImprovement && Math.abs(biggestImprovement.percentageChange) >= 10) {
    summary += `\n${biggestImprovement.name} significantly improved (+${Math.abs(biggestImprovement.percentageChange).toFixed(1)}%)`;
  }
  
  return summary;
}
